import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import styles from './index.module.scss';
import { V2PipelineLossProcessDataPostResponse } from '@maxtropy/device-customer-apis-v2';
import { Empty } from '@maxtropy/components';
enum Status {
  OVER = 1, // 超限
  NORMAL = 2, // 正常
}

const StatusDisplay = {
  [Status.OVER]: '超限',
  [Status.NORMAL]: '正常',
};

const WaterChart: React.FC<{ data?: V2PipelineLossProcessDataPostResponse; activeKey?: string }> = ({
  data,
  activeKey,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    if (!data) return;

    const Ddata = data?.data;

    chartInstance.current = echarts.init(chartRef.current);
    const biasItemSty = (fullColor: string, biasColor: string) => {
      return {
        color: fullColor,
        decal: {
          color: biasColor,
          dashArrayX: [1, 0],
          dashArrayY: [4, 3],
          rotation: -Math.PI / 4,
        },
      };
    };
    // 从 API 数据中提取图表数据
    const inputData: any[] = [];
    const outputData: any[] = [];
    const lossExceedData: any[] = [];
    const lossNormalData: any[] = [];

    Ddata?.forEach(item => {
      const ts = dayjs(item.ts).valueOf();

      // 损耗数据
      const loss = item.lossDataVo?.loss;
      const ifExceed = item.lossDataVo?.ifExceed || false;

      inputData.push([ts, item?.entryDataList?.[0]?.sum, []]);
      outputData.push([ts, item?.exitDataList?.[0]?.sum, []]);

      lossNormalData.push({
        value: [ts, !ifExceed ? loss : null],
        itemStyle: {
          color: 'rgba(255,203,71,0.4)',
          decal: {
            color: 'rgb(255,203,71)',
            dashArrayX: [1, 0],
            dashArrayY: [4, 3],
            rotation: -Math.PI / 4,
          },
        },
      });

      lossExceedData.push({
        value: [ts, ifExceed ? loss : null],
        itemStyle: {
          color: 'rgba(255, 77, 79, 0.4)',
          decal: {
            color: 'rgb(255, 77, 79)',
            dashArrayX: [1, 0],
            dashArrayY: [4, 3],
            rotation: -Math.PI / 4,
          },
        },
      });
    });

    const option: echarts.EChartsOption = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        backgroundColor: 'rgba(0, 20, 40, 0.9)',
        borderColor: 'rgba(0, 180, 220, 0.5)',
        textStyle: {
          color: '#fff',
        },
        formatter(items: any) {
          const { axisValue } = items[0];
          let str: string = dayjs(axisValue, 'x').format('YYYY-MM-DD');
          items.forEach((item: any) => {
            const { seriesName, data: itemData, marker } = item;
            if (seriesName.includes(StatusDisplay[Status.OVER]) || seriesName.includes(StatusDisplay[Status.NORMAL])) {
              if (!isNil(itemData.value?.[1])) {
                const unit = data.physicalUnitName || 'm³';
                const value = `${itemData.value[1].toFixed(2)}${unit}`;
                str += `<br> ${marker}<span style='display:inline-block;width:168px'>${seriesName}</span> ${value}`;
              } else {
                str += `<br> ${marker}<span style='display:inline-block;width:168px'>${seriesName}</span> --`;
              }
            } else {
              const unit = data.physicalUnitName || 'm³';
              const value = !isNil(itemData[1]) ? `${itemData[1].toFixed(2)}${unit}` : '--';
              str += `<br> ${marker}<span style='display:inline-block;width:168px'>${seriesName}</span>${value}`;
            }
          });
          return str;
        },
      },
      legend: {
        show: true,
        data: [
          { name: '输入', icon: 'rect' },
          { name: '输出', icon: 'rect' },
          { name: '损耗（超限）', icon: 'rect' },
          { name: '损耗（正常）', icon: 'rect' },
        ],
        top: 10,
        right: 20,
        bottom: 60,
        textStyle: {
          color: '#fff',
          fontSize: 12,
        },
        itemWidth: 14,
        itemHeight: 14,
      },
      grid: {
        left: '3%',
        right: '4%',
        top: 50,
        bottom: 20,
        containLabel: true,
      },
      xAxis: {
        type: 'time',
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: 'rgba(255, 255, 255, 1)',
          fontSize: 9,
          formatter: function (e: number, idx: number) {
            // 根据 activeKey 决定时间格式
            if (activeKey === 'DAY') {
              return dayjs(e, 'x').format('MM-DD');
            } else if (activeKey === 'WEEK') {
              return idx % 2 === 0 ? dayjs(e, 'x').format('YYYY-[W]w') : '';
            } else if (activeKey === 'YEAR') {
              return dayjs(e, 'x').format('YYYY-MM');
            }
            // 默认
            return dayjs(e, 'x').format('MM-DD');
          },
        },
      },
      yAxis: {
        type: 'value',
        name: data.physicalUnitName || 'm³',
        nameTextStyle: {
          color: 'rgba(255, 255, 255, 1)',
          fontSize: 12,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: 'rgba(255, 255, 255, 1)',
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.1)',
          },
        },
      },
      series: [
        {
          name: '输入',
          type: 'bar',
          color: '#2D8DFF',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: inputData,
        },
        {
          name: '输出',
          type: 'bar',
          stack: 'Ad',
          color: '#16DD8E',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: outputData,
        },
        {
          name: '损耗（超限）',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: lossExceedData,
          itemStyle: biasItemSty('rgba(255, 77, 79, 0.4)', 'rgb(255, 77, 79)'),
        },
        {
          name: '损耗（正常）',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          z: 0,
          data: lossNormalData,
          itemStyle: biasItemSty('rgba(255,203,71,0.4)', 'rgb(255,203,71)'),
        },
      ],
    };

    // 添加空数据提示
    if (!Ddata || Ddata.length === 0) {
      option.graphic = {
        type: 'text',
        left: 'center',
        top: 'middle',
        style: {
          text: '暂无数据',
          fontSize: 20,
          fill: 'rgba(255, 255, 255, 0.6)',
        },
      };
    }

    chartInstance.current.setOption(option);

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, [data]);

  if (!data) {
    return (
      <div className={styles.waterChart}>
        <Empty />
      </div>
    );
  }

  return (
    <>
      <div ref={chartRef} className={styles.waterChart} />
    </>
  );
};

export default WaterChart;
